import { CloseOutlined } from '@ant-design/icons';
import emailIcon from '@/components/Layouts/HeaderDropdown/icons/icon-email.svg';
import SvgIcon from '@/components/Layouts/HeaderDropdown/SvgIcon';
import { Modal } from 'antd';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import styles from './index.module.less';

const SUPPORT_DELAY_MS = 5000;
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';
const SUPPORT_EMAIL = 'erasaprotection@erasa.net';
const TELEGRAM_URL = 'https://t.me/erasa_emliy';

type PanelLayer = 'intro' | 'contact';

function TelegramContactIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g clipPath="url(#telegram-contact-icon-clip)">
        <path
          d="M18 9C18 11.3869 17.0518 13.6761 15.364 15.364C13.6761 17.0518 11.3869 18 9 18C6.61305 18 4.32387 17.0518 2.63604 15.364C0.948212 13.6761 0 11.3869 0 9C0 6.61305 0.948212 4.32387 2.63604 2.63604C4.32387 0.948212 6.61305 0 9 0C11.3869 0 13.6761 0.948212 15.364 2.63604C17.0518 4.32387 18 6.61305 18 9ZM9.32288 6.64425C8.44687 7.00875 6.69712 7.7625 4.07363 8.9055C3.64838 9.07425 3.42525 9.24 3.40425 9.40275C3.3705 9.67612 3.71363 9.78413 4.1805 9.9315L4.37738 9.99338C4.83638 10.143 5.45512 10.3174 5.77575 10.3241C6.06825 10.3316 6.39375 10.2116 6.75225 9.96413C9.204 8.30963 10.4693 7.47337 10.548 7.45537C10.6043 7.44187 10.683 7.42613 10.7347 7.47338C10.7865 7.52063 10.782 7.60838 10.7764 7.632C10.7426 7.77712 9.396 9.02812 8.69963 9.67612C8.4825 9.87862 8.32837 10.0215 8.29688 10.0541C8.22753 10.125 8.15702 10.1948 8.08537 10.2634C7.65787 10.6751 7.33838 10.9834 8.10225 11.4874C8.47013 11.7304 8.76488 11.9295 9.0585 12.1297C9.378 12.348 9.6975 12.5651 10.1115 12.8374C10.2165 12.9064 10.3177 12.9765 10.4153 13.0477C10.7876 13.3132 11.124 13.5518 11.5369 13.5135C11.7776 13.491 12.0262 13.266 12.1523 12.591C12.4504 10.9969 13.0365 7.54425 13.1715 6.12113C13.18 6.00283 13.1751 5.88395 13.1569 5.76675C13.146 5.67226 13.1002 5.58522 13.0286 5.52262C12.9261 5.45224 12.8042 5.41566 12.6799 5.418C12.3424 5.42362 11.8215 5.60475 9.32288 6.64425Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="telegram-contact-icon-clip">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function ContactCollapseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M4 6L8 10L12 6" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function useDesktopViewport() {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleChange = () => setIsDesktop(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
}

function SupportLauncher({
  showPrompt,
  onOpen,
  onDismissPrompt,
}: {
  showPrompt: boolean;
  onOpen: () => void;
  onDismissPrompt: () => void;
}) {
  const { t } = useTranslation('common');

  return (
    <div className={styles.launcher}>
      {showPrompt && (
        <div className={styles.promptBubble}>
          <button className={styles.promptAction} type="button" onClick={onOpen}>
            {t('Need a hand?')} <span aria-hidden="true">👋</span>
          </button>
          <button
            className={styles.promptClose}
            type="button"
            aria-label={t('Close support')}
            onClick={onDismissPrompt}
          >
            <CloseOutlined />
          </button>
        </div>
      )}
      <button className={styles.logoButton} type="button" aria-label={t('Open Erasa support')} onClick={onOpen}>
        <Image src="/images/layouts/erasa-logo.png" width={51} height={51} alt="" />
      </button>
    </div>
  );
}

function IntroPanel({ onClose, onGetHelp }: { onClose: () => void; onGetHelp: () => void }) {
  const { t } = useTranslation('common');

  return (
    <div className={styles.introPanel}>
      <button className={styles.introClose} type="button" aria-label={t('Close support')} onClick={onClose}>
        <CloseOutlined />
      </button>
      <p className={styles.introTitle}>
        {t('Need a hand?')} <span aria-hidden="true">👋</span>
      </p>
      <p className={styles.introDescription}>{t('Questions about plans, coverage, or takedowns? We can help.')}</p>
      <button className={styles.getHelpButton} type="button" onClick={onGetHelp}>
        {t('Get Help')}
      </button>
    </div>
  );
}

function ContactPanel({ isMobile, onCollapse }: { isMobile: boolean; onCollapse: () => void }) {
  const { t } = useTranslation('common');

  return (
    <div className={styles.contactPanel}>
      <div className={styles.contactHeader}>
        <p className={styles.supportTitle}>{t('Erasa Support')}</p>
        <div className={styles.replyStatus}>
          <span className={styles.statusDot} aria-hidden="true" />
          <span>{t('Typically replies under 55 minutes.')}</span>
        </div>
      </div>
      <button
        className={styles.contactClose}
        type="button"
        aria-label={isMobile ? t('Close support') : t('Minimize support')}
        onClick={onCollapse}
      >
        {isMobile ? (
          <span className={styles.mobileCloseGlyph} aria-hidden="true">
            ×
          </span>
        ) : (
          <ContactCollapseIcon />
        )}
      </button>

      <div className={styles.contactBody}>
        <div className={styles.agentBubble}>
          <p className={styles.contactTitle}>{t('How would you like to reach us?')}</p>
          <p className={styles.contactDescription}>{t('Choose whichever works best for you.')}</p>
        </div>
        <div className={styles.contactOptions}>
          <a className={styles.emailButton} href={`mailto:${SUPPORT_EMAIL}`}>
            <SvgIcon src={emailIcon} size={18} className={styles.contactIcon} />
            <span>{t('Email Us')}</span>
          </a>
          <a
            className={styles.telegramButton}
            href={TELEGRAM_URL}
            target={isMobile ? undefined : '_blank'}
            rel={isMobile ? undefined : 'noopener noreferrer'}
          >
            <span className={styles.contactIcon}>
              <TelegramContactIcon />
            </span>
            <span>{t('Telegram')}</span>
          </a>
        </div>
      </div>
    </div>
  );
}

export default function NeedHelp() {
  const { t } = useTranslation('common');
  const isDesktop = useDesktopViewport();
  const [isVisible, setIsVisible] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [layer, setLayer] = useState<PanelLayer>('intro');
  const [isIntroDismissed, setIsIntroDismissed] = useState(false);
  const [hasOpenedContact, setHasOpenedContact] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setIsVisible(true);
      setIsPanelOpen(window.matchMedia(DESKTOP_MEDIA_QUERY).matches);
    }, SUPPORT_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, []);

  if (!isVisible) {
    return null;
  }

  const handleGetHelp = () => {
    setLayer('contact');
    setHasOpenedContact(true);
    setShowPrompt(false);
  };

  const handleLauncherOpen = () => {
    if (isDesktop) {
      setLayer(hasOpenedContact ? 'contact' : 'intro');
    } else {
      setLayer('contact');
      setHasOpenedContact(true);
      setShowPrompt(false);
    }

    setIsPanelOpen(true);
  };

  const handleIntroClose = () => {
    setIsIntroDismissed(true);
    setIsPanelOpen(false);
  };

  const handleContactCollapse = () => {
    if (isDesktop && !isIntroDismissed) {
      setLayer('intro');
      return;
    }

    setIsPanelOpen(false);
  };

  if (isDesktop) {
    return (
      <div
        className={styles.desktopRoot}
        role={isPanelOpen ? 'dialog' : undefined}
        aria-label={isPanelOpen ? t('Erasa Support') : undefined}
      >
        {isPanelOpen ? (
          layer === 'intro' ? (
            <IntroPanel onClose={handleIntroClose} onGetHelp={handleGetHelp} />
          ) : (
            <ContactPanel isMobile={false} onCollapse={handleContactCollapse} />
          )
        ) : (
          <SupportLauncher showPrompt={false} onOpen={handleLauncherOpen} onDismissPrompt={() => undefined} />
        )}
      </div>
    );
  }

  return (
    <div className={styles.mobileRoot}>
      {!isPanelOpen && (
        <SupportLauncher
          showPrompt={showPrompt}
          onOpen={handleLauncherOpen}
          onDismissPrompt={() => setShowPrompt(false)}
        />
      )}
      <Modal
        className={`${styles.mobileModal} ${styles.mobileContactModal}`}
        wrapClassName={styles.mobileModalWrap}
        open={isPanelOpen}
        title={t('Erasa Support')}
        classNames={{ header: styles.visuallyHidden }}
        footer={null}
        closable={false}
        centered
        width={288}
        zIndex={10000}
        destroyOnClose
        onCancel={handleContactCollapse}
        styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.6)' } }}
      >
        <ContactPanel isMobile onCollapse={handleContactCollapse} />
      </Modal>
    </div>
  );
}
